import type { Account, AccountChange, Area, Blackout, PlanChange } from "./api";
import { blackoutProblem } from "./seasons";

export const WEEK_GROUPS = ["1", "2", "3", "4", "1-3", "2-4", "1-2-3-4"];

/** An imported plan can have a week group that the form does not offer. Keep it as a choice. */
export const weekGroupsFor = (current: string) =>
  current && !WEEK_GROUPS.includes(current) ? [...WEEK_GROUPS, current] : WEEK_GROUPS;

export function weekGroupLabel(group: string): string {
  if (group === "1-2-3-4") return "Every week";
  return group.includes("-") ? `Weeks ${group}` : `Week ${group}`;
}

export interface PlanForm {
  areaId: string;
  weekGroup: string;
  start: string;
  end: string;
  minutes: string;
  rate: string;
  /** "MM-DD", or both empty for all year. */
  seasonStart: string;
  seasonEnd: string;
  blackouts: Blackout[];
}

export function planForm(account: Account): PlanForm {
  const plan = account.plan;
  return {
    areaId: plan?.area_id ?? "",
    weekGroup: plan?.week_group ?? "",
    start: plan?.window?.[0] ?? "",
    end: plan?.window?.[1] ?? "",
    minutes: plan?.service_minutes ? String(plan.service_minutes) : "",
    rate: plan ? String(plan.stated_rate) : "",
    seasonStart: plan?.season?.[0] ?? "",
    seasonEnd: plan?.season?.[1] ?? "",
    blackouts: plan?.blackouts ?? [],
  };
}

/** Return the plan to send, null for no plan, or the text of the first problem. */
export function planChange(form: PlanForm, areas: Area[]): PlanChange | null | string {
  if (!form.areaId) return form.weekGroup || form.rate ? "Choose an area." : null;
  const area = areas.find((found) => found.id === form.areaId);
  if (!area) return "Choose an area.";
  const weekGroup = form.weekGroup || String(area.rotation_week);
  if (!/^[1-4](-[1-4]){0,3}$/.test(weekGroup)) return "Choose the weeks of the visits.";
  if (Boolean(form.start) !== Boolean(form.end)) return "Give the start and the end of the service window, or neither.";
  if (form.start && form.start >= form.end) return "The service window must end after it starts.";
  const rate = Number(form.rate);
  if (form.rate.trim() === "" || !Number.isFinite(rate) || rate < 0 || rate > 10_000) {
    return "Give the stated rate in gallons each week, from 0 to 10,000.";
  }
  const minutes = form.minutes.trim() === "" ? null : Number(form.minutes);
  if (minutes !== null && (!Number.isInteger(minutes) || minutes < 1 || minutes > 600)) {
    return "Give the minutes at the stop as a whole number from 1 to 600.";
  }
  if (Boolean(form.seasonStart) !== Boolean(form.seasonEnd)) return "Give the first and the last day of the season, or neither.";
  const blackout = blackoutProblem(form.blackouts);
  if (blackout) return blackout;
  return {
    area_id: area.id,
    week_group: weekGroup,
    window_start: form.start || null,
    window_end: form.end || null,
    service_minutes: minutes,
    stated_rate: rate,
    season_start: form.seasonStart || null,
    season_end: form.seasonEnd || null,
    blackouts: form.blackouts,
  };
}

/** The change that puts the account back as it was. None when the save added a plan (A-1). */
export function undoChange(before: Account, after: Account): AccountChange | null {
  const plan = before.plan;
  const commercial = before.commercial_status ? { commercial_status: before.commercial_status } : {};
  if (!plan) return after.plan ? null : { status: before.status, ...commercial };
  if (!plan.area_id) return null;
  return {
    status: before.status,
    ...commercial,
    plan: {
      area_id: plan.area_id,
      week_group: plan.week_group,
      window_start: plan.window?.[0] ?? null,
      window_end: plan.window?.[1] ?? null,
      service_minutes: plan.service_minutes ?? null,
      stated_rate: plan.stated_rate,
      season_start: plan.season?.[0] ?? null,
      season_end: plan.season?.[1] ?? null,
      blackouts: plan.blackouts ?? [],
    },
  };
}
